import React, { useEffect, useState } from 'react';
import excelNotificationService, { ExcelNotification } from '../services/excelNotificationService';
import { useAuth } from '../context/AuthContext';
import './ExcelNotificationDashboard.css';

interface NotificationDashboardProps {
  onClose: () => void;
}

const ExcelNotificationDashboard: React.FC<NotificationDashboardProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<ExcelNotification[]>([]);
  const [filteredNotifications, setFilteredNotifications] = useState<ExcelNotification[]>([]);
  const [filter, setFilter] = useState<'all' | 'unread' | 'assignments' | 'updates' | 'comments'>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  useEffect(() => {
    filterNotifications();
  }, [notifications, filter, searchTerm]);

  const loadNotifications = async () => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      const userNotifications = await excelNotificationService.getNotifications(user.uid);
      setNotifications(userNotifications.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ));
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterNotifications = () => {
    let filtered = [...notifications];

    // Filter by type
    switch (filter) {
      case 'unread':
        filtered = filtered.filter(n => !n.read);
        break;
      case 'assignments':
        filtered = filtered.filter(n => n.type === 'EXCEL_ASSIGNMENT');
        break;
      case 'updates':
        filtered = filtered.filter(n => n.type === 'EXCEL_UPDATE');
        break;
      case 'comments':
        filtered = filtered.filter(n => n.type === 'EXCEL_COMMENT');
        break;
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        n.data.sheetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (n.data.fieldName && n.data.fieldName.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredNotifications(filtered);
  };

  const handleNotificationClick = async (notification: ExcelNotification) => {
    if (!notification.read) {
      await excelNotificationService.markAsRead(notification.id!);
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
      );
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) return;
    
    try {
      await excelNotificationService.markAllAsRead(user.uid);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'EXCEL_UPDATE':
        return '📝';
      case 'EXCEL_ASSIGNMENT':
        return '👤';
      case 'EXCEL_COMMENT':
        return '💬';
      default:
        return '📊';
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'EXCEL_UPDATE':
        return '#007bff';
      case 'EXCEL_ASSIGNMENT':
        return '#28a745';
      case 'EXCEL_COMMENT':
        return '#ffc107';
      default:
        return '#6c757d';
    }
  };

  const getFilterCount = (filterType: string) => {
    switch (filterType) {
      case 'unread':
        return notifications.filter(n => !n.read).length;
      case 'assignments':
        return notifications.filter(n => n.type === 'EXCEL_ASSIGNMENT').length;
      case 'updates':
        return notifications.filter(n => n.type === 'EXCEL_UPDATE').length;
      case 'comments':
        return notifications.filter(n => n.type === 'EXCEL_COMMENT').length;
      default:
        return notifications.length;
    }
  };

  if (!user) return null;

  return (
    <div className="excel-notification-dashboard">
      <div className="dashboard-header">
        <h2>Excel Notifications</h2>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="dashboard-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        <div className="filter-tabs">
          {[
            { key: 'all', label: 'All' },
            { key: 'unread', label: 'Unread' },
            { key: 'assignments', label: 'Assignments' },
            { key: 'updates', label: 'Updates' },
            { key: 'comments', label: 'Comments' }
          ].map(tab => (
            <button
              key={tab.key}
              className={`filter-tab ${filter === tab.key ? 'active' : ''}`}
              onClick={() => setFilter(tab.key as any)}
            >
              {tab.label} ({getFilterCount(tab.key)})
            </button>
          ))}
        </div>

        <div className="action-buttons">
          <button className="refresh-btn" onClick={loadNotifications}>
            🔄 Refresh
          </button>
          {notifications.some(n => !n.read) && (
            <button className="mark-all-read-btn" onClick={handleMarkAllAsRead}>
              ✓ Mark all as read
            </button>
          )}
        </div>
      </div>

      <div className="notifications-container">
        {isLoading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>No notifications found</h3>
            <p>
              {filter === 'all' 
                ? "You don't have any notifications yet."
                : `No ${filter} notifications found.`
              }
            </p>
          </div>
        ) : (
          <div className="notifications-list">
            {filteredNotifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-card ${!notification.read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-header">
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-title">
                    {notification.title}
                  </div>
                  <div className="notification-time">
                    {formatTimestamp(notification.createdAt)}
                  </div>
                </div>

                <div className="notification-body">
                  <div className="notification-message">
                    {notification.message}
                  </div>

                  <div className="notification-details">
                    <div className="detail-row">
                      <span className="detail-label">Sheet:</span>
                      <span className="detail-value">{notification.data.sheetName}</span>
                    </div>

                    {notification.data.fieldName && (
                      <div className="detail-row">
                        <span className="detail-label">Field:</span>
                        <span className="detail-value">{notification.data.fieldName}</span>
                      </div>
                    )}

                    {notification.data.fieldValue && (
                      <div className="detail-row">
                        <span className="detail-label">Value:</span>
                        <span className="detail-value">{notification.data.fieldValue}</span>
                      </div>
                    )}

                    {notification.data.assignedBy && (
                      <div className="detail-row">
                        <span className="detail-label">Assigned by:</span>
                        <span className="detail-value">{notification.data.assignedBy}</span>
                      </div>
                    )}

                    {notification.data.cellAddress && (
                      <div className="detail-row">
                        <span className="detail-label">Location:</span>
                        <span className="detail-value">{notification.data.cellAddress}</span>
                      </div>
                    )}

                    {notification.data.itemId && (
                      <div className="detail-row">
                        <span className="detail-label">Item ID:</span>
                        <span className="detail-value">{notification.data.itemId}</span>
                      </div>
                    )}
                  </div>
                </div>

                {!notification.read && (
                  <div className="unread-indicator"></div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelNotificationDashboard;



















